import React, { useState, useEffect, useRef } from "react";
import { apiService } from "../../services/apiService";
import {
  ActivityDefinition,
  Workflow,
  GoogleDriveAttachment,
} from "../../types";
import { GoogleDriveFilePicker } from "./GoogleDriveFilePicker";
import ProjectFolderSelectModal from "./ProjectFolderSelectModal";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import toast from "../../utils/toast";
import {
  XMarkIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  FolderIcon,
  DocumentTextIcon,
  ClockIcon,
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

interface WorkflowInstanceItem {
  id: number;
  name: string;
  projectName: string;
  googleDriveFolderId?: string | null;
  description?: string;
  workflowId?: number;
  nodesSnapshot?: string;
  selectedPaftaStartNodes?: string[] | null;
  allowedAdHocActivityIds?: number[] | null;
  activitySelectionConfigured?: boolean;
}

interface ActivityInstanceSummary {
  id: number;
  templateActivityId?: number;
  activityTitle?: string;
  addedByUserId?: number;
  addedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  completedAt?: string;
  submitDescription?: string;
  userFormData?: string;
  forms?: Array<{
    id: number;
    templateFormId?: number | null;
  }>;
}

interface AddActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onDailyHoursUpdate?: (status: DailyHoursStatus) => void;
  userId: number;
}

type BreakEntry = {
  startTime: string;
  endTime: string;
};

type DailyHoursStatus = {
  dailyTargetHours?: number;
  dailyWorkedHours?: number;
  dailyRemainingHours?: number;
  dailyOverHours?: number;
  isWorkday?: boolean;
  isHalfDay?: boolean;
};

type LocalUploadFile = File & {
  webkitRelativePath?: string;
};

type SelectedLocalFile = {
  id: string;
  file: LocalUploadFile;
  folderId: string | null;
  folderPath: string;
  folderPathEntries: FolderPathEntry[];
};

type SelectedDriveAttachment = GoogleDriveAttachment & {
  targetFolderId: string | null;
  targetFolderPath: string;
  targetFolderPathEntries: FolderPathEntry[];
};

type FolderPathEntry = {
  id: string | null;
  name: string;
};

const AddActivityModal: React.FC<AddActivityModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onDailyHoursUpdate,
  userId,
}) => {
  const { t } = useTranslation(["activities", "errors", "common", "dashboard", "projects"]);
  const { user, getSubmitDescriptionLabel, getSubmitDescriptionPlaceholder } =
    useAuth();
  const projectFilesRootLabel = t("projects:projectFilesModal.rootFolder");
  const submitDescriptionLabel =
    getSubmitDescriptionLabel(t("activities:description"));
  const submitDescriptionPlaceholder = getSubmitDescriptionPlaceholder(
    t("activities:submitDescriptionPlaceholder"),
  );

  // Generate time options in 15-minute intervals (00:00 - 23:45)
  const generateTimeOptions = (): string[] => {
    const times: string[] = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const h = hour.toString().padStart(2, "0");
        const m = minute.toString().padStart(2, "0");
        times.push(`${h}:${m}`);
      }
    }
    return times;
  };

  const [step, setStep] = useState(1);
  const [workflows, setWorkflows] = useState<WorkflowInstanceItem[]>([]);
  const [activities, setActivities] = useState<ActivityDefinition[]>([]);
  const [selectedWorkflowData, setSelectedWorkflowData] =
    useState<Workflow | null>(null);

  const [selectedWorkflowId, setSelectedWorkflowId] = useState<number | null>(
    null
  );
  const [selectedActivity, setSelectedActivity] =
    useState<ActivityDefinition | null>(null);
  const [activityStartTime, setActivityStartTime] = useState<string>("");
  const [activityEndTime, setActivityEndTime] = useState<string>("");
  const [activityStartDate, setActivityStartDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [activityEndDate, setActivityEndDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [activityBreaks, setActivityBreaks] = useState<BreakEntry[]>([]);
  const [submitDescription, setSubmitDescription] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [workflowSearchTerm, setWorkflowSearchTerm] = useState("");
  const [formSubmissions, setFormSubmissions] = useState<Record<number, any>>(
    {}
  );
  const [selectSearchByField, setSelectSearchByField] = useState<
    Record<string, string>
  >({});
  const [lastActivityLoading, setLastActivityLoading] = useState(false);
  const [lastActivityError, setLastActivityError] = useState<string | null>(
    null
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // File upload state
  const [selectedFiles, setSelectedFiles] = useState<SelectedLocalFile[]>([]);
  const [driveAttachments, setDriveAttachments] = useState<
    SelectedDriveAttachment[]
  >([]);
  const [fileDescriptions, setFileDescriptions] = useState<
    Record<string, string>
  >({});
  const [showDrivePicker, setShowDrivePicker] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedFolderPath, setSelectedFolderPath] = useState<string>(
    projectFilesRootLabel
  );
  const [selectedFolderPathEntries, setSelectedFolderPathEntries] = useState<
    FolderPathEntry[]
  >([{ id: null, name: projectFilesRootLabel }]);
  const [showFolderSelectModal, setShowFolderSelectModal] = useState(false);
  const [workflowRootFolderNames, setWorkflowRootFolderNames] = useState<
    Record<number, string>
  >({});
  const selectedWorkflowIdRef = useRef<number | null>(null);
  const folderInputRef = useRef<HTMLInputElement | null>(null);
  const createLocalFileId = (file: File): string =>
    `${file.name}-${file.size}-${file.lastModified}-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;

  const buildRootPathEntries = (rootLabel: string): FolderPathEntry[] => [
    { id: null, name: rootLabel },
  ];
  const getLocalFileRelativeFolderPath = (file: File): string => {
    const relativePath = (file as LocalUploadFile).webkitRelativePath
      ?.replace(/\\/g, "/")
      .trim();
    if (!relativePath) {
      return "";
    }
    const segments = relativePath.split("/").filter(Boolean);
    segments.pop();
    return segments.join("/");
  };
  const getLocalFileRootFolderName = (file: File): string => {
    const relativePath = (file as LocalUploadFile).webkitRelativePath
      ?.replace(/\\/g, "/")
      .trim();
    if (!relativePath) {
      return "";
    }
    return relativePath.split("/").filter(Boolean)[0] || "";
  };
  const getLocalFileDisplayPath = (file: File): string => {
    const relativePath = (file as LocalUploadFile).webkitRelativePath
      ?.replace(/\\/g, "/")
      .trim();
    return relativePath || file.name;
  };
  const getFileTargetFolderDisplayPath = (
    selectedFile: SelectedLocalFile
  ): string => {
    const basePath =
      selectedFile.folderPath ||
      selectedFile.folderPathEntries
        .map((entry) => entry.name)
        .filter(Boolean)
        .join(" / ") ||
      projectFilesRootLabel;
    const relativeFolderPath = getLocalFileRelativeFolderPath(selectedFile.file);
    if (!relativeFolderPath) {
      return basePath;
    }
    return `${basePath} / ${relativeFolderPath
      .split("/")
      .filter(Boolean)
      .join(" / ")}`;
  };
  const normalizeFolderName = (value?: string | null): string =>
    String(value || "").trim().toLocaleLowerCase("tr-TR");
  const buildUniqueFolderName = (
    baseName: string,
    existingFolderNames: Set<string>
  ): string => {
    const trimmedBaseName = baseName.trim() || "Klasör";
    let candidate = trimmedBaseName;
    let suffix = 2;

    while (existingFolderNames.has(normalizeFolderName(candidate))) {
      candidate = `${trimmedBaseName} (${suffix})`;
      suffix += 1;
    }

    existingFolderNames.add(normalizeFolderName(candidate));
    return candidate;
  };
  const buildSelectedLocalFiles = (files: File[]): SelectedLocalFile[] =>
    files.map((file) => ({
      id: createLocalFileId(file),
      file: file as LocalUploadFile,
      folderId: selectedFolderId,
      folderPath: selectedFolderPath,
      folderPathEntries: [...selectedFolderPathEntries],
    }));
  const buildSelectedDriveAttachments = (
    attachments: GoogleDriveAttachment[]
  ): SelectedDriveAttachment[] =>
    attachments.map((attachment) => ({
      ...attachment,
      targetFolderId: selectedFolderId,
      targetFolderPath: selectedFolderPath,
      targetFolderPathEntries: [...selectedFolderPathEntries],
    }));
  const resolveUploadFolderIdForFile = async (
    workflowInstanceId: number,
    selectedFile: SelectedLocalFile,
    folderCache: Map<string, string | null>,
    rootFolderNameCache: Map<string, string>
  ): Promise<string | null> => {
    const relativeFolderPath = getLocalFileRelativeFolderPath(selectedFile.file);
    if (!relativeFolderPath) {
      return selectedFile.folderId;
    }

    const segments = relativeFolderPath.split("/").filter(Boolean);
    const baseFolderKey = selectedFile.folderId ?? "__root__";
    let currentParentId = selectedFile.folderId ?? null;
    let currentPath = "";

    for (const segment of segments) {
      currentPath = currentPath ? `${currentPath}/${segment}` : segment;
      const cacheKey = `${baseFolderKey}::${currentPath}`;
      if (folderCache.has(cacheKey)) {
        currentParentId = folderCache.get(cacheKey) ?? null;
        continue;
      }

      let folderNameToCreate = segment;
      if (!currentPath.includes("/")) {
        const rootFolderName = getLocalFileRootFolderName(selectedFile.file);
        if (rootFolderName) {
          const rootFolderCacheKey = `${baseFolderKey}::${normalizeFolderName(
            rootFolderName
          )}`;
          if (rootFolderNameCache.has(rootFolderCacheKey)) {
            folderNameToCreate =
              rootFolderNameCache.get(rootFolderCacheKey) || segment;
          } else {
            const existingItems = await apiService.getWorkflowInstanceFiles(
              workflowInstanceId,
              currentParentId
            );
            const existingFolderNames = new Set(
              existingItems
                .filter(
                  (existingFile) =>
                    existingFile.mimeType ===
                    "application/vnd.google-apps.folder"
                )
                .map((existingFolder) => normalizeFolderName(existingFolder.name))
            );
            folderNameToCreate = buildUniqueFolderName(
              segment,
              existingFolderNames
            );
            rootFolderNameCache.set(rootFolderCacheKey, folderNameToCreate);
          }
        }
      }

      const createdFolder = await apiService.createProjectFolder(
        workflowInstanceId,
        folderNameToCreate,
        currentParentId
      );
      currentParentId = createdFolder.id;
      folderCache.set(cacheKey, currentParentId);
    }

    return currentParentId;
  };

  useEffect(() => {
    if (isOpen) {
      loadData();
    } else {
      // Reset state when modal closes
      selectedWorkflowIdRef.current = null;
      setStep(1);
      setSelectedWorkflowId(null);
      setSelectedActivity(null);
      setActivityStartTime("");
      setActivityEndTime("");
      const today = new Date().toISOString().split("T")[0];
      setActivityStartDate(today);
      setActivityEndDate(today);
      setActivityBreaks([]);
      setSubmitDescription("");
      setSearchTerm("");
      setWorkflowSearchTerm("");
      setFormSubmissions({});
      setSelectSearchByField({});
      setLastActivityLoading(false);
      setLastActivityError(null);
      setSelectedFiles([]);
      setDriveAttachments([]);
      setFileDescriptions({});
      setSelectedFolderId(null);
      setSelectedFolderPath(projectFilesRootLabel);
      setSelectedFolderPathEntries(buildRootPathEntries(projectFilesRootLabel));
      setShowFolderSelectModal(false);
      setError(null);
    }
  }, [isOpen, userId]);

  const loadData = async () => {
    try {
      const [workflowsData, activitiesData] = await Promise.all([
        apiService.getUserWorkflowInstances(userId),
        apiService.getAllActivities({ activeOnly: true }),
      ]);

      setWorkflows(workflowsData);
      setActivities(activitiesData);
    } catch (err) {
      setError(t("errors:loadFailed"));
    }
  };

  const resolveWorkflowRootLabel = (instance?: WorkflowInstanceItem | null) => {
    if (!instance) {
      return projectFilesRootLabel;
    }
    return (
      workflowRootFolderNames[instance.id] ||
      instance.projectName ||
      instance.name ||
      projectFilesRootLabel
    );
  };

  const loadWorkflowRootFolderName = async (
    instance?: WorkflowInstanceItem | null
  ): Promise<string | null> => {
    if (!instance?.id || !instance.googleDriveFolderId) {
      return null;
    }

    const cachedName = workflowRootFolderNames[instance.id];
    if (cachedName) {
      return cachedName;
    }

    try {
      const folderInfo = await apiService.getGoogleDriveFileInfo(
        instance.googleDriveFolderId
      );
      const folderName = folderInfo?.name?.trim();
      if (folderName) {
        setWorkflowRootFolderNames((prev) => ({
          ...prev,
          [instance.id]: folderName,
        }));
        return folderName;
      }
    } catch (error) {
    }

    return null;
  };

  const extractActivityIdsFromWorkflowData = (
    workflowData?: Workflow | null
  ): Set<number> => {
    const ids = new Set<number>();
    if (!workflowData?.nodes) {
      return ids;
    }

    workflowData.nodes.forEach((node) => {
      const activityId = (node.data as any)?.activityDefinitionId;
      const parsedId = Number(activityId);
      if (!Number.isNaN(parsedId)) {
        ids.add(parsedId);
      }
    });

    return ids;
  };

  const buildInstanceActivityIdSet = (
    instance?: WorkflowInstanceItem | null
  ): Set<number> => {
    const ids = new Set<number>();
    parseNodesSnapshotActivityIds(instance?.nodesSnapshot).forEach((id) =>
      ids.add(id)
    );

    const adHocIds = instance?.allowedAdHocActivityIds || [];
    adHocIds.forEach((id) => ids.add(id));

    return ids;
  };

  const buildEffectiveAllowedActivityIdSet = (
    instance?: WorkflowInstanceItem | null,
    workflowData?: Workflow | null
  ): Set<number> => {
    const workflowIds = extractActivityIdsFromWorkflowData(workflowData);
    if (workflowIds.size > 0) {
      return workflowIds;
    }
    return buildInstanceActivityIdSet(instance);
  };

  const handleWorkflowSelect = async (workflowId: number) => {
    selectedWorkflowIdRef.current = workflowId;
    setSelectedWorkflowId(workflowId);
    setSelectedActivity(null);
    setSelectedFolderId(null);
    setSearchTerm("");
    const instance = workflows.find((w) => w.id === workflowId);
    const rootLabel = resolveWorkflowRootLabel(instance);
    setSelectedFolderPath(rootLabel);
    setSelectedFolderPathEntries(buildRootPathEntries(rootLabel));
    setLastActivityError(null);

    void loadWorkflowRootFolderName(instance).then((driveRootFolderName) => {
      if (
        driveRootFolderName &&
        selectedWorkflowIdRef.current === workflowId
      ) {
        setSelectedFolderPath(driveRootFolderName);
        setSelectedFolderPathEntries(buildRootPathEntries(driveRootFolderName));
      }
    });

    // Find the selected workflow instance
    const selectedInstance = workflows.find((w) => w.id === workflowId);
    let workflowData: Workflow | null = null;

    // If workflowId exists, fetch workflow details
    if (selectedInstance?.workflowId) {
      try {
        workflowData = await apiService.getWorkflowById(
          selectedInstance.workflowId
        );
        setSelectedWorkflowData(workflowData);
      } catch (err) {
        setSelectedWorkflowData(null);
      }
    } else {
      setSelectedWorkflowData(null);
    }

    const nextAllowedActivityIds = buildEffectiveAllowedActivityIdSet(
      selectedInstance,
      workflowData
    );
    const nextActivities = activities.filter((activity) =>
      nextAllowedActivityIds.has(activity.id)
    );

    if (nextActivities.length === 1) {
      handleActivitySelect(nextActivities[0]);
      return;
    }

    setStep(2);
    setError(null);
  };

  const handleActivitySelect = (activity: ActivityDefinition) => {
    setSelectedActivity(activity);
    setLastActivityError(null);
    // Reset time inputs when selecting a new activity
    setActivityStartTime("");
    setActivityEndTime("");
    setActivityBreaks([]);
    setStep(3);
    setError(null);
  };

  const parseNodesSnapshotActivityIds = (nodesSnapshot?: string): number[] => {
    if (!nodesSnapshot) return [];
    try {
      const parsed = JSON.parse(nodesSnapshot);
      if (!Array.isArray(parsed)) return [];
      const ids = parsed
        .map((node: any) => node?.activityId)
        .map((id: any) => Number(id))
        .filter((id: number) => !Number.isNaN(id));
      return Array.from(new Set(ids));
    } catch (error) {
      return [];
    }
  };

  const updateFormSubmission = (
    formId: number,
    elementId: string,
    value: any
  ) => {
    setFormSubmissions((prev) => ({
      ...prev,
      [formId]: {
        ...prev[formId],
        [elementId]: value,
      },
    }));
  };

  const normalizeToStringArray = (value: any): string[] => {
    if (Array.isArray(value)) {
      return value
        .filter((item) => item !== undefined && item !== null && item !== "")
        .map((item) => String(item));
    }
    if (value === undefined || value === null || value === "") {
      return [];
    }
    return [String(value)];
  };

  const commitStringSelection = (
    formId: number,
    elementId: string,
    selectedValues: string[]
  ) => {
    const uniqueValues = Array.from(new Set(selectedValues));
    if (uniqueValues.length === 0) {
      updateFormSubmission(formId, elementId, "");
    } else if (uniqueValues.length === 1) {
      updateFormSubmission(formId, elementId, uniqueValues[0]);
    } else {
      updateFormSubmission(formId, elementId, uniqueValues);
    }
  };

  const getSelectSearchKey = (formId: number, elementId: string): string =>
    `${formId}:${elementId}`;

  const formatDurationHours = (hours: number): number => {
    return Math.round(hours * 100) / 100;
  };

  const parseDateTimeInput = (date: string, time: string): Date | null => {
    if (!date || !time) return null;
    const value = new Date(`${date}T${time}`);
    return isNaN(value.getTime()) ? null : value;
  };

  const parseDateTimeString = (value?: string): Date | null => {
    if (!value) return null;
    const normalized = value.includes("T") ? value : value.replace(" ", "T");
    const parsed = new Date(normalized);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const calculateDurationHours = (
    startDate: string,
    startTime: string,
    endDate: string,
    endTime: string
  ): number => {
    const start = parseDateTimeInput(startDate, startTime);
    const end = parseDateTimeInput(endDate, endTime);
    if (!start || !end) return 0;
    const diffMs = end.getTime() - start.getTime();
    if (diffMs <= 0) return 0;
    return diffMs / (1000 * 60 * 60);
  };

  const buildBreakRanges = (
    breaks: BreakEntry[],
    startDate: string,
    endDate: string
  ): Array<{ start: string; end: string }> => {
    if (!startDate || !endDate) return [];
    return breaks
      .filter((entry) => entry.startTime && entry.endTime)
      .map((entry) => {
        let endDateForEntry = startDate;
        if (startDate !== endDate) {
          const times = generateTimeOptions();
          const startIndex = times.indexOf(entry.startTime);
          const endIndex = times.indexOf(entry.endTime);
          if (startIndex !== -1 && endIndex !== -1 && endIndex < startIndex) {
            endDateForEntry = endDate;
          }
        }
        return {
          start: `${startDate} ${entry.startTime}`,
          end: `${endDateForEntry} ${entry.endTime}`,
        };
      });
  };

  const calculateBreakHoursFromRanges = (
    breaks: Array<{ start: string; end: string }>,
    rangeStart?: string,
    rangeEnd?: string
  ): number => {
    if (!breaks || breaks.length === 0) return 0;
    const activityStart = parseDateTimeString(rangeStart);
    const activityEnd = parseDateTimeString(rangeEnd);

    const intervals = breaks
      .map((entry) => {
        const start = parseDateTimeString(entry.start);
        const end = parseDateTimeString(entry.end);
        if (!start || !end || end <= start) return null;
        let adjustedStart = start;
        let adjustedEnd = end;
        if (activityStart && adjustedStart < activityStart) {
          adjustedStart = activityStart;
        }
        if (activityEnd && adjustedEnd > activityEnd) {
          adjustedEnd = activityEnd;
        }
        if (adjustedEnd <= adjustedStart) return null;
        return { start: adjustedStart, end: adjustedEnd };
      })
      .filter(Boolean) as Array<{ start: Date; end: Date }>;

    if (intervals.length === 0) return 0;
    intervals.sort((a, b) => a.start.getTime() - b.start.getTime());
    let totalMs = 0;
    let current = intervals[0];
    for (let i = 1; i < intervals.length; i += 1) {
      const next = intervals[i];
      if (next.start <= current.end) {
        if (next.end > current.end) {
          current.end = next.end;
        }
      } else {
        totalMs += current.end.getTime() - current.start.getTime();
        current = next;
      }
    }
    totalMs += current.end.getTime() - current.start.getTime();
    return totalMs / (1000 * 60 * 60);
  };

  const getBreakValidationError = (
    breaks: BreakEntry[],
    startDate: string,
    startTime: string,
    endDate: string,
    endTime: string
  ): string | null => {
    if (!breaks.length) return null;
    const activityStart = parseDateTimeInput(startDate, startTime);
    const activityEnd = parseDateTimeInput(endDate, endTime);
    if (!activityStart || !activityEnd) return null;

    for (const entry of breaks) {
      const hasAnyValue = entry.startTime || entry.endTime;
      if (!hasAnyValue) continue;
      if (!entry.startTime || !entry.endTime) {
        return t("dashboard:breakIncomplete");
      }
      const start = parseDateTimeInput(startDate, entry.startTime);
      const endDateForEntry =
        startDate !== endDate && entry.endTime < entry.startTime
          ? endDate
          : startDate;
      const end = parseDateTimeInput(endDateForEntry, entry.endTime);
      if (!start || !end || end <= start) {
        return t("dashboard:breakInvalidRange");
      }
      if (start < activityStart || end > activityEnd) {
        return t("dashboard:breakOutsideRange");
      }
    }
    return null;
  };

  const getNetDurationWithBreaks = (
    startDate: string,
    startTime: string,
    endDate: string,
    endTime: string,
    breaks: BreakEntry[]
  ): { baseHours: number; breakHours: number; netHours: number } => {
    const baseHours = calculateDurationHours(
      startDate,
      startTime,
      endDate,
      endTime
    );
    if (baseHours <= 0) {
      return { baseHours: 0, breakHours: 0, netHours: 0 };
    }
    const breakRanges = buildBreakRanges(breaks, startDate, endDate);
    const breakHours = calculateBreakHoursFromRanges(
      breakRanges,
      `${startDate} ${startTime}`,
      `${endDate} ${endTime}`
    );
    const netHours = Math.max(baseHours - breakHours, 0);
    return { baseHours, breakHours, netHours };
  };

  const getBreakTimeOptions = (): string[] => {
    const times = generateTimeOptions();
    if (!activityStartTime || !activityEndTime) return times;
    const startIndex = times.indexOf(activityStartTime);
    const endIndex = times.indexOf(activityEndTime);
    if (startIndex === -1 || endIndex === -1) return times;
    if (startIndex <= endIndex) {
      return times.slice(startIndex, endIndex + 1);
    }
    return [...times.slice(startIndex), ...times.slice(0, endIndex + 1)];
  };

  const parseJsonSafely = (value?: string | null): any => {
    if (!value || typeof value !== "string") return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      return JSON.parse(trimmed);
    } catch (err) {
      return null;
    }
  };

  const getUserFormDataFromActivity = (
    activity: ActivityInstanceSummary,
    userIdValue: number
  ): Record<string, any> => {
    const parsed = parseJsonSafely(activity.userFormData);
    if (!parsed || typeof parsed !== "object") return {};
    const userKey = String(userIdValue);
    const userData = (parsed as Record<string, any>)[userKey];
    if (userData && typeof userData === "object") {
      return userData as Record<string, any>;
    }
    return {};
  };

  const extractDescriptionValue = (entry: any): string | null => {
    if (!entry) return null;
    if (typeof entry === "string") {
      const trimmed = entry.trim();
      return trimmed ? trimmed : null;
    }
    if (typeof entry === "object") {
      const candidates = [
        entry.description,
        entry.submitDescription,
        entry.comment,
        entry.note,
      ];
      const found = candidates.find(
        (item) => typeof item === "string" && item.trim()
      );
      return found ? found.trim() : null;
    }
    return null;
  };

  const extractSubmitDescriptionForUser = (
    raw?: string | null,
    userIdValue?: number
  ): string => {
    if (!raw) return "";
    const trimmed = raw.trim();
    if (!trimmed) return "";
    if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
      const parsed = parseJsonSafely(trimmed);
      if (Array.isArray(parsed)) {
        let fallback = "";
        let matched = "";
        parsed.forEach((entry) => {
          const description = extractDescriptionValue(entry);
          if (!description) return;
          fallback = description;
          if (
            entry &&
            typeof entry === "object" &&
            userIdValue !== undefined &&
            String(entry.userId) === String(userIdValue)
          ) {
            matched = description;
          }
        });
        return matched || fallback;
      }
      if (parsed && typeof parsed === "object") {
        const description = extractDescriptionValue(parsed);
        return description || "";
      }
      return "";
    }
    return trimmed;
  };

  const getActivitySortTime = (activity: ActivityInstanceSummary): number => {
    const candidate =
      activity.addedAt ||
      activity.completedAt ||
      activity.updatedAt ||
      activity.createdAt;
    if (!candidate) return 0;
    const time = Date.parse(candidate);
    return Number.isNaN(time) ? 0 : time;
  };

  const findLastActivityForUser = (
    instances: ActivityInstanceSummary[],
    userIdValue: number
  ): ActivityInstanceSummary | null => {
    const candidates = instances.filter(
      (instance) => instance.addedByUserId === userIdValue
    );
    if (candidates.length === 0) return null;
    return candidates.sort(
      (a, b) => getActivitySortTime(b) - getActivitySortTime(a)
    )[0];
  };

  const buildFormSubmissionsFromActivity = (
    activity: ActivityInstanceSummary,
    activityDefinition: ActivityDefinition,
    userIdValue: number
  ): Record<number, any> => {
    const userFormData = getUserFormDataFromActivity(activity, userIdValue);
    if (!userFormData || typeof userFormData !== "object") {
      return {};
    }

    const templateIds = new Set<number>(
      activityDefinition.activityForms.map((entry) => entry.form.id)
    );

    const instanceToTemplate = new Map<number, number>();
    activity.forms?.forEach((form) => {
      if (form.templateFormId) {
        instanceToTemplate.set(form.id, form.templateFormId);
      }
    });

    const submissions: Record<number, any> = {};
    Object.entries(userFormData).forEach(([key, value]) => {
      const keyNumber = Number(key);
      if (Number.isNaN(keyNumber)) return;
      if (templateIds.has(keyNumber)) {
        submissions[keyNumber] = value;
        return;
      }
      const templateId = instanceToTemplate.get(keyNumber);
      if (templateId && templateIds.has(templateId)) {
        submissions[templateId] = value;
      }
    });

    return submissions;
  };

  const addBreakEntry = () => {
    if (
      !activityStartTime ||
      !activityEndTime ||
      !activityStartDate ||
      !activityEndDate
    ) {
      return;
    }
    setActivityBreaks((prev) => [
      ...prev,
      {
        startTime: "",
        endTime: "",
      },
    ]);
  };

  const updateBreakEntry = (
    index: number,
    field: keyof BreakEntry,
    value: string
  ) => {
    setActivityBreaks((prev) =>
      prev.map((entry, idx) =>
        idx === index ? { ...entry, [field]: value } : entry
      )
    );
  };

  const removeBreakEntry = (index: number) => {
    setActivityBreaks((prev) => prev.filter((_, idx) => idx !== index));
  };

  const durationInfo = getNetDurationWithBreaks(
    activityStartDate,
    activityStartTime,
    activityEndDate,
    activityEndTime,
    activityBreaks
  );
  const calculatedDuration = formatDurationHours(durationInfo.netHours);
  const breakTimeOptions = getBreakTimeOptions();
  const canAddBreak = Boolean(
    activityStartTime && activityEndTime && activityStartDate && activityEndDate
  );

  const handleSubmit = async () => {
    if (!selectedWorkflowId || !selectedActivity) {
      setError(t("activities:missingFields"));
      return;
    }

    // Validate time inputs
    if (
      !activityStartTime ||
      !activityEndTime ||
      !activityStartDate ||
      !activityEndDate
    ) {
      setError(t("dashboard:enterStartEndTime"));
      return;
    }
    const breakError = getBreakValidationError(
      activityBreaks,
      activityStartDate,
      activityStartTime,
      activityEndDate,
      activityEndTime
    );
    if (breakError) {
      setError(breakError);
      return;
    }
    if (calculatedDuration <= 0) {
      setError(t("dashboard:invalidDuration"));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Validate overlap first to avoid uploading files for a request that will be rejected.
      const formattedStartTime = `${activityStartDate} ${activityStartTime}`;
      const formattedEndTime = `${activityEndDate} ${activityEndTime}`;
      await apiService.validateWorkTimeRange(
        userId,
        formattedStartTime,
        formattedEndTime
      );

      // Upload files if any and collect attachment info
      const uploadedAttachments: GoogleDriveAttachment[] = [];
      const copiedDriveAttachments: GoogleDriveAttachment[] = [];
      if (selectedFiles.length > 0 || driveAttachments.length > 0) {
        setUploadingFiles(true);
        try {
          if (selectedFiles.length > 0) {
            const folderCache = new Map<string, string | null>();
            const rootFolderNameCache = new Map<string, string>();
            for (const selectedFile of selectedFiles) {
              const description = fileDescriptions[selectedFile.id];
              const targetFolderId = await resolveUploadFolderIdForFile(
                selectedWorkflowId,
                selectedFile,
                folderCache,
                rootFolderNameCache
              );
              const uploadedFile = await apiService.uploadFileToProject(
                selectedWorkflowId,
                selectedFile.file,
                description,
                undefined,
                undefined,
                targetFolderId
              );
              uploadedAttachments.push(uploadedFile);
            }
          }

          if (driveAttachments.length > 0) {
            for (const attachment of driveAttachments) {
              const description = fileDescriptions[attachment.id];
              const copiedFile = await apiService.copyDriveFileToProject(
                selectedWorkflowId,
                attachment.id,
                description,
                attachment.targetFolderId
              );
              copiedDriveAttachments.push(copiedFile);
            }
          }
        } catch (uploadError: any) {
          setError(t("dashboard:fileUploadFailed"));
          setUploadingFiles(false);
          setLoading(false);
          return;
        }
        setUploadingFiles(false);
      }

      const breakRanges = buildBreakRanges(
        activityBreaks,
        activityStartDate,
        activityEndDate
      );

      // Merge uploaded files and Drive attachments with descriptions
      const allAttachments = [
        ...uploadedAttachments,
        ...copiedDriveAttachments.map((att) => ({
          ...att,
          description: fileDescriptions[att.id],
          uploadedAt: new Date().toISOString(),
          uploadedBy: user?.id,
          uploadedByName: user
            ? `${user.firstName} ${user.lastName}`
            : undefined,
        })),
      ].map((att) => ({
        ...att,
        addedBy: userId || 0,
        addedAt: att.addedAt || new Date().toISOString(),
      }));

      const response = await apiService.addActivityToWorkflow(
        selectedWorkflowId,
        selectedActivity.id,
        userId,
        formSubmissions,
        calculatedDuration,
        submitDescription,
        {
          startTime: formattedStartTime,
          endTime: formattedEndTime,
          breaks: breakRanges.length > 0 ? breakRanges : undefined,
        },
        allAttachments.length > 0 ? allAttachments : undefined
      );

      if (onDailyHoursUpdate) {
        onDailyHoursUpdate({
          dailyTargetHours: response?.dailyTargetHours,
          dailyWorkedHours: response?.dailyWorkedHours,
          dailyRemainingHours: response?.dailyRemainingHours,
          dailyOverHours: response?.dailyOverHours,
          isWorkday: response?.isWorkday,
        });
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      const backendMessage = err.response?.data?.message;
      if (
        backendMessage ===
        "Work time overlaps with another activity on the same day."
      ) {
        setError(t("dashboard:workTimeOverlap"));
      } else {
        setError(backendMessage || t("activities:addActivityError"));
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedWorkflowInstance = workflows.find(
    (workflow) => workflow.id === selectedWorkflowId
  );
  const selectedWorkflowRootLabel = resolveWorkflowRootLabel(
    selectedWorkflowInstance
  );
  const currentWorkflowActivityIdSet =
    extractActivityIdsFromWorkflowData(selectedWorkflowData);
  const instanceActivityIdSet =
    buildInstanceActivityIdSet(selectedWorkflowInstance);
  const workflowActivityIdSet =
    currentWorkflowActivityIdSet.size > 0
      ? currentWorkflowActivityIdSet
      : instanceActivityIdSet;
  const allowedActivityIdSet = buildEffectiveAllowedActivityIdSet(
    selectedWorkflowInstance,
    selectedWorkflowData
  );
  const allowedActivities = activities.filter((activity) =>
    allowedActivityIdSet.has(activity.id)
  );
  const shouldSkipActivitySelection = allowedActivities.length === 1;

  const handleUseLastActivity = async () => {
    if (!selectedWorkflowId) return;
    if (!userId) {
      setLastActivityError(t("dashboard:lastActivityNotFound"));
      return;
    }
    setLastActivityLoading(true);
    setLastActivityError(null);

    try {
      const activityInstances: ActivityInstanceSummary[] =
        await apiService.getActivityInstancesByWorkflowInstance(
          selectedWorkflowId
        );
      const lastActivity = findLastActivityForUser(activityInstances, userId);

      if (!lastActivity) {
        setLastActivityError(t("dashboard:lastActivityNotFound"));
        return;
      }

      const activityDefinition = activities.find(
        (activity) => activity.id === lastActivity.templateActivityId
      );

      if (!activityDefinition) {
        setLastActivityError(t("dashboard:lastActivityNotAvailable"));
        return;
      }

      if (
        allowedActivityIdSet &&
        !allowedActivityIdSet.has(activityDefinition.id)
      ) {
        setLastActivityError(t("dashboard:lastActivityNotAvailable"));
        return;
      }

      setSelectedActivity(activityDefinition);
      setSubmitDescription(
        extractSubmitDescriptionForUser(
          lastActivity.submitDescription,
          userId
        )
      );
      setFormSubmissions(
        buildFormSubmissionsFromActivity(lastActivity, activityDefinition, userId)
      );

      setActivityStartTime("");
      setActivityEndTime("");
      setActivityBreaks([]);
      setSelectedFiles([]);
      setDriveAttachments([]);
      setFileDescriptions({});
      setStep(3);
    } catch (err) {
      setLastActivityError(t("errors:loadFailed"));
    } finally {
      setLastActivityLoading(false);
    }
  };

  const isActivityInWorkflow = (activityId: number): boolean =>
    workflowActivityIdSet.has(activityId);

  const normalizeSearchText = (value?: string | null): string =>
    String(value || "")
      .toLocaleLowerCase("tr-TR")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\u0131/g, "i")
      .trim();

  const workflowQuery = normalizeSearchText(workflowSearchTerm);
  const activityQuery = normalizeSearchText(searchTerm);

  const filteredWorkflows = workflows.filter(
    (workflow) =>
      normalizeSearchText(workflow.projectName).includes(workflowQuery) ||
      normalizeSearchText(workflow.name).includes(workflowQuery) ||
      normalizeSearchText(workflow.description).includes(workflowQuery)
  );

  const filteredActivities = allowedActivities
    .filter(
      (activity) =>
        normalizeSearchText(activity.title).includes(activityQuery) ||
        normalizeSearchText(activity.description).includes(activityQuery) ||
        normalizeSearchText(activity.tags).includes(activityQuery)
    )
    .sort((a, b) => {
      const aInWorkflow = isActivityInWorkflow(a.id);
      const bInWorkflow = isActivityInWorkflow(b.id);
      // Show activities in workflow first
      if (aInWorkflow && !bInWorkflow) return -1;
      if (!aInWorkflow && bInWorkflow) return 1;
      return 0;
    });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
        <div className="bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 px-6 py-3 relative overflow-hidden">
          <div className="relative flex items-center justify-between gap-4">
            <div className="min-w-0 flex items-center gap-3">
              {step > 1 && (
                <button
                  onClick={() => {
                    if (step === 3 && shouldSkipActivitySelection) {
                      setStep(1);
                      return;
                    }
                    setStep(step - 1);
                  }}
                  className="text-white/80 hover:text-white hover:bg-white/20 p-1.5 rounded-md transition-all"
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                </button>
              )}
              <h2 className="text-base sm:text-lg font-semibold text-white truncate">
                {t("activities:addNewActivity")}
              </h2>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 text-[11px] text-green-100/80">
                <span
                  className={`px-2 py-0.5 rounded-full ${
                    step >= 1 ? "bg-white/20 text-white" : "opacity-60"
                  }`}
                >
                  {t("common:project")}
                </span>
                <span
                  className={`px-2 py-0.5 rounded-full ${
                    step >= 2 ? "bg-white/20 text-white" : "opacity-60"
                  }`}
                >
                  {t("activities:activity")}
                </span>
                <span
                  className={`px-2 py-0.5 rounded-full ${
                    step >= 3 ? "bg-white/20 text-white" : "opacity-60"
                  }`}
                >
                  {t("common:details")}
                </span>
              </div>
              <span className="text-[11px] text-green-100/90">{step}/3</span>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white hover:bg-white/20 p-2 rounded-lg transition-all"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg animate-in slide-in-from-top-2 duration-200">
              <div className="flex items-center">
                <XMarkIcon className="w-5 h-5 text-red-500 mr-2" />
                <p className="text-red-700 font-medium">{error}</p>
              </div>
            </div>
          )}
          {/* Step 1: Select Project/Workflow */}
          {step === 1 && (
            <div className="animate-in fade-in slide-in-from-right-5 duration-300">
              {/* Search Bar */}
              {workflows.length > 0 && (
                <div className="relative mb-6">
                  <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Proje ara..."
                    value={workflowSearchTerm}
                    onChange={(e) => setWorkflowSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                  />
                </div>
              )}

              {workflows.length === 0 ? (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-2xl mb-4">
                    <FolderIcon className="w-10 h-10 text-gray-400" />
                  </div>
                  <p className="text-gray-500 text-lg font-medium">
                    {t("activities:noProjectsYet")}
                  </p>
                  <p className="text-gray-400 text-sm mt-2">
                    {t("activities:noWorkflowInstances")}
                  </p>
                </div>
              ) : filteredWorkflows.length === 0 ? (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-2xl mb-4">
                    <FolderIcon className="w-10 h-10 text-gray-400" />
                  </div>
                  <p className="text-gray-500 text-lg font-medium">
                    {t("projects:projectNotFound")}
                  </p>
                  <p className="text-gray-400 text-sm mt-2">
                    {t("dashboard:noSearchResultsFor", { query: workflowSearchTerm })}
                  </p>
                </div>
              ) : (
                <div className="max-h-[700px] overflow-y-auto border border-gray-200 rounded-lg">
                  <div className="divide-y divide-gray-100">
                    {filteredWorkflows.map((workflow) => (
                      <button
                        key={workflow.id}
                        onClick={() => handleWorkflowSelect(workflow.id)}
                        className="w-full px-4 py-2.5 bg-white hover:bg-green-50 transition-all duration-200 text-left"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <h4 className="font-semibold text-gray-900 text-sm truncate flex-1 min-w-0">
                            {workflow.projectName}
                          </h4>
                          <p className="text-xs text-gray-500 truncate max-w-[40%]">
                            {workflow.name}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Select Activity */}
          {step === 2 && (
            <div className="animate-in fade-in slide-in-from-right-5 duration-300">
              {selectedWorkflowInstance && (
                <div className="flex items-center justify-start mb-2">
                  <div className="text-xs text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                    {t("common:project")}:{" "}
                    {selectedWorkflowInstance.projectName ||
                      selectedWorkflowInstance.name}
                  </div>
                </div>
              )}
              <div className="mb-6 flex flex-col items-center">
                <button
                  type="button"
                  onClick={handleUseLastActivity}
                  disabled={lastActivityLoading}
                  className="flex items-center justify-center space-x-2 px-4 py-2 bg-green-50 text-green-700 border border-green-200 rounded-xl hover:bg-green-100 transition-colors text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {lastActivityLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-green-300 border-t-green-600 rounded-full animate-spin" />
                      <span>{t("common:loading")}</span>
                    </>
                  ) : (
                    <>
                      <ClockIcon className="w-4 h-4" />
                      <span>{t("dashboard:useLastActivity")}</span>
                    </>
                  )}
                </button>
                {lastActivityError && (
                  <p className="text-xs text-red-600 mt-2">
                    {lastActivityError}
                  </p>
                )}
              </div>

              {/* Search Bar */}
              <div className="relative mb-6">
                <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={t("activities:searchActivity")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                />
              </div>

              <div className="max-h-[700px] overflow-y-auto border border-gray-200 rounded-lg">
                {filteredActivities.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500 font-medium">
                      {t("activities:activityNotFound")}
                    </p>
                    <p className="text-gray-400 text-sm mt-1">
                      {t("activities:tryDifferentSearch")}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {filteredActivities.map((activity) => (
                      <button
                        key={activity.id}
                        onClick={() => handleActivitySelect(activity)}
                        className="w-full px-4 py-2.5 bg-white hover:bg-green-50 transition-all duration-200 text-left"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-900 text-sm truncate">
                              {activity.title}
                            </h4>
                            {activity.description && (
                              <p className="text-xs text-gray-600 truncate mt-0.5">
                                {activity.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Details */}
          {step === 3 && selectedActivity && (
            <div className="animate-in fade-in slide-in-from-right-5 duration-300">
              {selectedWorkflowInstance && (
                <div className="flex items-center justify-start mb-2">
                  <div className="text-xs text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                    {t("common:project")}:{" "}
                    {selectedWorkflowInstance.projectName ||
                      selectedWorkflowInstance.name}
                  </div>
                </div>
              )}
              {/* Selected Activity Summary */}
              <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="font-semibold text-gray-800 text-sm">
                  {selectedActivity.title}
                </div>
                {selectedActivity.description && (
                  <p className="text-xs text-gray-600 mt-1">
                    {selectedActivity.description}
                  </p>
                )}
              </div>

              {/* Start/End Time Input */}
              <div className="mb-6">
                <label className="block text-gray-800 font-semibold mb-2">
                  {t("dashboard:enterStartEndTime")}
                </label>

                {/* Start Date & Time */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      {t("dashboard:startDate")}
                    </label>
                    <input
                      type="date"
                      value={activityStartDate}
                      onChange={(e) => setActivityStartDate(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      {t("dashboard:startTime")}
                    </label>
                    <select
                      value={activityStartTime}
                      onChange={(e) => setActivityStartTime(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                    >
                      <option value="">{t("dashboard:selectTime")}</option>
                      {generateTimeOptions().map((time) => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* End Date & Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      {t("dashboard:endDate")}
                    </label>
                    <input
                      type="date"
                      value={activityEndDate}
                      onChange={(e) => setActivityEndDate(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      {t("dashboard:endTime")}
                    </label>
                    <select
                      value={activityEndTime}
                      onChange={(e) => setActivityEndTime(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                    >
                      <option value="">{t("dashboard:selectTime")}</option>
                      {generateTimeOptions().map((time) => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Breaks Section */}
                <div className="mt-5 border-t border-green-200 pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-800">
                        {t("dashboard:breaksTitle")}
                      </h4>
                      <p className="text-xs text-gray-600">
                        {t("dashboard:breaksDesc")}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={addBreakEntry}
                      disabled={!canAddBreak}
                      className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg transition-colors ${
                        canAddBreak
                          ? "text-green-700 bg-green-100 hover:bg-green-200"
                          : "text-gray-400 bg-gray-100 cursor-not-allowed"
                      }`}
                    >
                      <PlusIcon className="w-4 h-4" />
                      {t("dashboard:addBreak")}
                    </button>
                  </div>

                  {activityBreaks.length > 0 && (
                    <div className="mt-4 space-y-3">
                      {activityBreaks.map((entry, index) => (
                        <div
                          key={`break-${index}`}
                          className="bg-white border border-green-100 rounded-lg p-4"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-semibold text-gray-700">
                              {t("dashboard:breakItem")} {index + 1}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeBreakEntry(index)}
                              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-red-600"
                            >
                              <TrashIcon className="w-4 h-4" />
                              {t("common:remove")}
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                {t("dashboard:startTime")}
                              </label>
                              <select
                                value={entry.startTime}
                                onChange={(e) =>
                                  updateBreakEntry(
                                    index,
                                    "startTime",
                                    e.target.value
                                  )
                                }
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent"
                              >
                                <option value="">{t("dashboard:selectTime")}</option>
                                {breakTimeOptions.map((time) => (
                                  <option key={time} value={time}>
                                    {time}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                {t("dashboard:endTime")}
                              </label>
                              <select
                                value={entry.endTime}
                                onChange={(e) =>
                                  updateBreakEntry(
                                    index,
                                    "endTime",
                                    e.target.value
                                  )
                                }
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent"
                              >
                                <option value="">{t("dashboard:selectTime")}</option>
                                {breakTimeOptions.map((time) => (
                                  <option key={time} value={time}>
                                    {time}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Show calculated duration */}
                {activityStartTime &&
                  activityEndTime &&
                  activityStartDate &&
                  activityEndDate && (
                    <div className="mt-3 p-3 bg-green-50 rounded-xl border border-green-200">
                      <p className="text-sm text-green-700">
                        <span className="font-medium">
                          {activityBreaks.length > 0
                            ? t("dashboard:totalDuration")
                            : t("dashboard:calculatedDuration")}
                          :
                        </span>
                        <span className="ml-2">
                          {formatDurationHours(
                            activityBreaks.length > 0
                              ? durationInfo.baseHours
                              : durationInfo.netHours
                          )}{" "}
                          {t("dashboard:hours")}
                        </span>
                      </p>
                      {activityBreaks.length > 0 && (
                        <div className="mt-2 text-xs text-green-700 flex items-center justify-between">
                          <span>{t("dashboard:breakTotal")}:</span>
                          <span className="font-semibold">
                            {formatDurationHours(durationInfo.breakHours)}{" "}
                            {t("dashboard:hours")}
                          </span>
                        </div>
                      )}
                      {activityBreaks.length > 0 && (
                        <div className="mt-1 text-xs text-green-700 flex items-center justify-between">
                          <span>{t("dashboard:netDuration")}:</span>
                          <span className="font-semibold">
                            {calculatedDuration} {t("dashboard:hours")}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
              </div>

              {/* Description Input */}
              <div className="mb-6">
                <label className="block text-gray-800 font-semibold mb-2">
                  {submitDescriptionLabel}
                </label>
                <textarea
                  value={submitDescription}
                  onChange={(e) => setSubmitDescription(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all resize-none"
                  rows={3}
                  placeholder={submitDescriptionPlaceholder}
                />
              </div>

              {/* Forms Section */}
              {selectedActivity.activityForms &&
                selectedActivity.activityForms.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-lg font-bold text-gray-800 mb-4">
                      {t("activities:fillForms")}
                    </h4>
                    <div className="space-y-4">
                      {selectedActivity.activityForms
                        .sort((a, b) => a.displayOrder - b.displayOrder)
                        .map((activityForm, index) => {
                          const form = activityForm.form;
                          return (
                            <div
                              key={form.id}
                              className="p-5 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl animate-in fade-in-50 slide-in-from-bottom-2"
                              style={{ animationDelay: `${index * 100}ms` }}
                            >
                              <h5 className="text-base font-bold text-gray-800 mb-3 flex items-center justify-between">
                                <span>
                                  {form.title}
                                  {activityForm.isRequired && (
                                    <span className="text-red-500 ml-1">*</span>
                                  )}
                                </span>
                                <span className="text-xs font-normal text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                  #{activityForm.displayOrder}
                                </span>
                              </h5>
                              {form.description && (
                                <p className="text-sm text-gray-600 mb-4">
                                  {form.description}
                                </p>
                              )}
                              <div className="space-y-3">
                                {form.formStructure?.elements?.map(
                                  (element: any) => (
                                    <div key={element.id}>
                                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        {element.label}
                                        {element.required && (
                                          <span className="text-red-500 ml-1">
                                            *
                                          </span>
                                        )}
                                      </label>
                                      {element.type === "textarea" ? (
                                        <textarea
                                          className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all resize-none"
                                          rows={3}
                                          placeholder={
                                            element.placeholder ||
                                            t("activities:enterField", {
                                              label: element.label,
                                            })
                                          }
                                          value={
                                            formSubmissions[form.id]?.[
                                              element.id
                                            ] || ""
                                          }
                                          onChange={(e) =>
                                            updateFormSubmission(
                                              form.id,
                                              element.id,
                                              e.target.value
                                            )
                                          }
                                          required={element.required}
                                        />
                                      ) : element.type === "select" ? (
                                        <div>
                                          {(() => {
                                            const allowMultiple = Boolean(
                                              (element as any).allowMultiple
                                            );
                                            const allOptions = (
                                              element.options || []
                                            ) as string[];
                                            const selectedValues =
                                              normalizeToStringArray(
                                                formSubmissions[form.id]?.[
                                                  element.id
                                                ]
                                              );

                                            if (allOptions.length === 0) {
                                              return (
                                                <div className="text-sm text-gray-500">
                                                  {t(
                                                    "forms:noOptions",
                                                    "Seçenek yok"
                                                  )}
                                                </div>
                                              );
                                            }

                                            if (!allowMultiple) {
                                              return (
                                                <select
                                                  value={selectedValues[0] || ""}
                                                  onChange={(event) =>
                                                    updateFormSubmission(
                                                      form.id,
                                                      element.id,
                                                      event.target.value
                                                    )
                                                  }
                                                  className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                                                >
                                                  <option value="">
                                                    {t(
                                                      "activities:selectOption",
                                                      "Seçenek seçin"
                                                    )}
                                                  </option>
                                                  {allOptions.map((option) => (
                                                    <option key={option} value={option}>
                                                      {option}
                                                    </option>
                                                  ))}
                                                </select>
                                              );
                                            }

                                            const searchKey = getSelectSearchKey(
                                              form.id,
                                              element.id
                                            );
                                            const searchValue =
                                              selectSearchByField[searchKey] || "";
                                            const normalizedSearch = searchValue
                                              .trim()
                                              .toLocaleLowerCase("tr-TR");
                                            const filteredOptions =
                                              normalizedSearch.length > 0
                                                ? allOptions.filter((option) =>
                                                    option
                                                      .toLocaleLowerCase("tr-TR")
                                                      .includes(normalizedSearch)
                                                  )
                                                : allOptions;

                                            return (
                                              <div className="space-y-2 rounded-md border border-gray-300 bg-white p-3 max-h-52 overflow-y-auto">
                                                <input
                                                  type="text"
                                                  value={searchValue}
                                                  onChange={(event) =>
                                                    setSelectSearchByField((prev) => ({
                                                      ...prev,
                                                      [searchKey]: event.target.value,
                                                    }))
                                                  }
                                                  placeholder={t(
                                                    "common:search",
                                                    "Ara..."
                                                  )}
                                                  className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                                                />
                                                {filteredOptions.length === 0 ? (
                                                  <div className="text-sm text-gray-500">
                                                    {t(
                                                      "common:noResults",
                                                      "Sonuç bulunamadı"
                                                    )}
                                                  </div>
                                                ) : (
                                                  filteredOptions.map(
                                                    (option: string) => (
                                                      <label
                                                        key={option}
                                                        className="flex items-center"
                                                      >
                                                        <input
                                                          type="checkbox"
                                                          checked={selectedValues.includes(
                                                            option
                                                          )}
                                                          onChange={(e) => {
                                                            const nextValues = e
                                                              .target.checked
                                                              ? [
                                                                  ...selectedValues,
                                                                  option,
                                                                ]
                                                              : selectedValues.filter(
                                                                  (item) =>
                                                                    item !== option
                                                                );
                                                            commitStringSelection(
                                                              form.id,
                                                              element.id,
                                                              nextValues
                                                            );
                                                          }}
                                                          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                                                        />
                                                        <span className="ml-2 text-sm text-gray-700">
                                                          {option}
                                                        </span>
                                                      </label>
                                                    )
                                                  )
                                                )}
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      ) : (
                                        <input
                                          type={element.type || "text"}
                                          className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                                          placeholder={
                                            element.placeholder ||
                                            t("activities:enterField", {
                                              label: element.label,
                                            })
                                          }
                                          value={
                                            formSubmissions[form.id]?.[
                                              element.id
                                            ] || ""
                                          }
                                          onChange={(e) =>
                                            updateFormSubmission(
                                              form.id,
                                              element.id,
                                              e.target.value
                                            )
                                          }
                                          required={element.required}
                                        />
                                      )}
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}

              {/* File Upload Section */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-gray-800 font-semibold">
                    {t("dashboard:attachFiles")}
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => folderInputRef.current?.click()}
                      className="flex items-center space-x-2 text-sm text-amber-700 hover:text-amber-800 font-medium bg-amber-50 hover:bg-amber-100 px-3 py-1.5 rounded-lg transition-colors"
                      disabled={uploadingFiles || loading}
                    >
                      <FolderIcon className="w-4 h-4" />
                      <span>{t("dashboard:uploadProjectFiles.folderFromComputer")}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowDrivePicker(true)}
                      className="flex items-center space-x-2 text-sm text-green-600 hover:text-green-700 font-medium bg-green-50 hover:bg-green-100 px-3 py-1.5 rounded-lg transition-colors"
                    >
                      <PlusIcon className="w-4 h-4" />
                      <span>{t("dashboard:uploadProjectFiles.addFromDrive")}</span>
                    </button>
                  </div>
                </div>
                <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {t("dashboard:uploadProjectFiles.targetFolder")}
                    </p>
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {selectedFolderPath}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {t("dashboard:uploadProjectFiles.folderFromComputerHint")}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setShowFolderSelectModal(true)}
                      className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      disabled={!selectedWorkflowId || uploadingFiles || loading}
                    >
                      {t("dashboard:uploadProjectFiles.selectFolder")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedFolderId(null);
                        setSelectedFolderPath(selectedWorkflowRootLabel);
                        setSelectedFolderPathEntries(
                          buildRootPathEntries(selectedWorkflowRootLabel)
                        );
                      }}
                      className="px-3 py-2 rounded-lg bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      disabled={uploadingFiles || loading}
                      title={t("dashboard:uploadProjectFiles.backToRootFolder")}
                    >
                      {t("dashboard:uploadProjectFiles.reset")}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-center w-full">
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-green-300 border-dashed rounded-xl cursor-pointer bg-green-50 hover:bg-green-100 transition-colors">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <svg
                        className="w-8 h-8 mb-3 text-green-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">
                          {t("dashboard:clickToUpload")}
                        </span>
                      </p>
                      <p className="text-xs text-gray-500">
                        {t("dashboard:uploadFileTypes")}
                      </p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      multiple
                      onChange={(e) => {
                        if (e.target.files) {
                          const nextFiles = buildSelectedLocalFiles(
                            Array.from(e.target.files)
                          );
                          setSelectedFiles((prev) => [...prev, ...nextFiles]);
                          e.target.value = "";
                        }
                      }}
                    />
                  </label>
                  <input
                    ref={folderInputRef}
                    type="file"
                    className="hidden"
                    multiple
                    onChange={(e) => {
                      if (e.target.files) {
                        const nextFiles = buildSelectedLocalFiles(
                          Array.from(e.target.files)
                        );
                        setSelectedFiles((prev) => [...prev, ...nextFiles]);
                        e.target.value = "";
                      }
                    }}
                    disabled={uploadingFiles || loading}
                    {...({ webkitdirectory: "", directory: "" } as any)}
                  />
                </div>

                {/* Selected Files List */}
                {(selectedFiles.length > 0 || driveAttachments.length > 0) && (
                  <div className="mt-4 space-y-2">
                    <h4 className="text-sm font-medium text-gray-700">
                      {t("dashboard:selectedFiles")} (
                      {selectedFiles.length + driveAttachments.length})
                    </h4>
                    <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                      {selectedFiles.map((selectedFile) => {
                        return (
                          <div
                            key={selectedFile.id}
                            className="bg-white p-3 rounded-lg border border-green-100 space-y-2"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3 flex-1 min-w-0">
                                <DocumentTextIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-gray-700 truncate">
                                    {selectedFile.file.name}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {(selectedFile.file.size / 1024 / 1024).toFixed(2)} MB
                                  </p>
                                  {getLocalFileDisplayPath(selectedFile.file) !==
                                    selectedFile.file.name && (
                                    <p className="text-[11px] text-gray-400 truncate">
                                      {getLocalFileDisplayPath(selectedFile.file)}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={() => {
                                  setSelectedFiles((prev) =>
                                    prev.filter(
                                      (current) => current.id !== selectedFile.id
                                    )
                                  );
                                  setFileDescriptions((prev) => {
                                    const next = { ...prev };
                                    delete next[selectedFile.id];
                                    return next;
                                  });
                                }}
                                className="ml-2 text-red-500 hover:text-red-700 p-1 flex-shrink-0"
                              >
                                <XMarkIcon className="w-5 h-5" />
                              </button>
                            </div>

                            <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                              <div className="min-w-0">
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                  {t("dashboard:uploadProjectFiles.fileFolder")}
                                </p>
                                <p className="text-xs text-gray-700 truncate">
                                  {getFileTargetFolderDisplayPath(selectedFile)}
                                </p>
                              </div>
                            </div>

                            <input
                              type="text"
                              placeholder={t("dashboard:uploadProjectFiles.optionalDescription")}
                              value={fileDescriptions[selectedFile.id] || ""}
                              onChange={(e) =>
                                setFileDescriptions((prev) => ({
                                  ...prev,
                                  [selectedFile.id]: e.target.value,
                                }))
                              }
                              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            />
                          </div>
                        );
                      })}
                      {driveAttachments.map((att) => (
                        <div
                          key={`drive-${att.id}`}
                          className="bg-white p-3 rounded-lg border border-blue-100 space-y-2"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              {att.iconUrl ? (
                                <img
                                  src={att.iconUrl}
                                  alt=""
                                  className="w-5 h-5 flex-shrink-0"
                                />
                              ) : (
                                <DocumentTextIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
                              )}
                              <div className="min-w-0 flex-1">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const previewWindow =
                                      apiService.shouldOpenPreviewWindow(att.name, att.mimeType)
                                        ? window.open("", "_blank")
                                        : null;
                                    if (previewWindow) {
                                      previewWindow.opener = null;
                                      apiService.preparePreviewWindow(previewWindow, att.name);
                                    }
                                    void apiService
                                      .previewGoogleDriveFile(att.id, att.name, previewWindow)
                                      .catch(() => toast.error("Dosya görüntülenemedi."));
                                  }}
                                  className="text-sm font-medium text-blue-600 hover:underline truncate block text-left"
                                >
                                  {att.name}
                                </button>
                                <p className="text-xs text-gray-500">
                                  Google Drive
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() =>
                                setDriveAttachments((prev) =>
                                  prev.filter((a) => a.id !== att.id)
                                )
                              }
                              className="ml-2 text-red-500 hover:text-red-700 p-1 flex-shrink-0"
                            >
                              <XMarkIcon className="w-5 h-5" />
                            </button>
                          </div>
                          <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                            <div className="min-w-0">
                              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                {t("dashboard:uploadProjectFiles.fileFolder")}
                              </p>
                              <p className="text-xs text-gray-700 truncate">
                                {att.targetFolderPath || selectedWorkflowRootLabel}
                              </p>
                            </div>
                            <FolderIcon className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          </div>
                          <input
                            type="text"
                            placeholder={t("dashboard:uploadProjectFiles.optionalDescription")}
                            value={fileDescriptions[att.id] || ""}
                            onChange={(e) =>
                              setFileDescriptions((prev) => ({
                                ...prev,
                                [att.id]: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Google Drive Picker */}
              <GoogleDriveFilePicker
                isOpen={showDrivePicker}
                onClose={() => setShowDrivePicker(false)}
                onSelect={(files) => {
                  setDriveAttachments((prev) => [
                    ...prev,
                    ...buildSelectedDriveAttachments(files),
                  ]);
                  setShowDrivePicker(false);
                }}
              />

              <ProjectFolderSelectModal
                isOpen={showFolderSelectModal}
                onClose={() => setShowFolderSelectModal(false)}
                workflowInstanceId={selectedWorkflowId}
                initialFolderId={selectedFolderId}
                initialPath={selectedFolderPathEntries}
                rootLabel={selectedWorkflowRootLabel}
                onSelect={(folderId, path) => {
                  const label = (path || [])
                    .map((p) => p.name)
                    .filter(Boolean)
                    .join(" / ");
                  const resolvedPath = label || selectedWorkflowRootLabel;
                  const nextPathEntries = path || [];
                  setSelectedFolderId(folderId);
                  setSelectedFolderPath(resolvedPath);
                  setSelectedFolderPathEntries(nextPathEntries);
                }}
              />

              {/* Action Buttons */}
              <div className="flex items-center justify-end pt-4 border-t border-gray-200">
                <button
                  onClick={handleSubmit}
                  className="flex items-center justify-center space-x-2 px-6 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl shadow-lg shadow-green-500/30 hover:shadow-xl hover:shadow-green-500/40 transition-all font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || uploadingFiles}
                >
                  {loading || uploadingFiles ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>
                        {uploadingFiles
                          ? t("dashboard:uploading")
                          : t("common:adding")}
                        ...
                      </span>
                    </>
                  ) : (
                    <>
                      <CheckIcon className="w-5 h-5" />
                      <span>{t("activities:addActivity")}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddActivityModal;
